import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, Users, Download, AlertTriangle, SearchX, ChevronDown, LayoutGrid, Zap } from "lucide-react";
import { Corners } from "./Hero";

const SORTS = [
  ["latest", "Latest"],
  ["players", "Most players"],
  ["bonus", "Highest bonus"],
  ["name", "A to Z"],
];

const playersNum = (p) => parseFloat(String(p).replace("K", "")) || 0;

const Meter = ({ value, max }) => {
  const seg = Math.max(1, Math.round((value / max) * 10));
  return (
    <span className="flex gap-[3px] ml-2" aria-hidden="true">
      {Array.from({ length: 10 }).map((_, i) => (
        <span key={i} className={`w-1 sm:w-1.5 h-2.5 sm:h-3 rounded-[2px] ${i < seg ? "bg-[#ffb224]" : "bg-[#1c5f35]"}`} />
      ))}
    </span>
  );
};

export const GameCard = ({ game, rank, downloadUrl, maxPlayers = 1000 }) => {
  const navigate = useNavigate();
  const onDownload = (e) => {
    e.stopPropagation();
    if (downloadUrl) window.open(downloadUrl, "_blank", "noopener");
    else navigate(`/game/${game.slug}`);
  };
  return (
    <article
      data-testid={`game-card-${game.slug}`}
      onClick={() => navigate(`/game/${game.slug}`)}
      className="group relative overflow-hidden bg-gradient-to-br from-[#0b4d24] via-[#073c1b] to-[#052b14] border border-[#1c5f35] rounded-[20px] p-3 sm:p-4 cursor-pointer select-none hover:border-[#ffb224]/70 hover:shadow-[0_18px_40px_-18px_rgba(255,178,36,0.45)] hover:-translate-y-0.5 active:scale-[0.975] active:border-[#ffb224] transition-all duration-200 [-webkit-tap-highlight-color:transparent]"
      style={{ animation: "fadeUp 0.45s ease both", animationDelay: `${(rank % 14) * 35}ms` }}
    >
      <Corners inside />
      <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-[-20deg] group-hover:left-[150%] group-active:left-[150%] transition-[left] duration-700 ease-out" />
      <span data-testid="game-card-rank" className="absolute top-3 right-3 sm:top-3.5 sm:right-3.5 text-[10px] sm:text-[11px] font-extrabold text-[#ffb224]/80 bg-[#013013] border border-[#1c5f35] px-2 py-0.5 rounded-full">#{rank}</span>

      <div className="flex items-center gap-3 sm:gap-3.5">
        <span className="relative shrink-0">
          <img src={game.logo} alt={`${game.name} APK`} loading="lazy" className="w-[60px] h-[60px] sm:w-[76px] sm:h-[76px] rounded-2xl object-cover ring-2 ring-[#1c5f35] group-hover:ring-[#ffb224] group-active:ring-[#ffb224] group-hover:scale-105 transition-all duration-300" />
          <span aria-hidden="true" className="absolute -inset-1 rounded-[20px] bg-[#ffb224]/20 blur-md opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity -z-10" />
        </span>
        <div className="min-w-0 flex-1 pr-8">
          <h3 className="text-white font-extrabold text-[17px] sm:text-lg leading-tight truncate group-hover:text-[#ffb224] transition-colors">{game.name}</h3>
          <p data-testid="game-card-players" className="flex items-center text-emerald-50/75 text-[13px] sm:text-sm mt-1 whitespace-nowrap">
            <Users size={15} className="shrink-0 mr-1.5 text-[#ffb224]" /> {game.players} players
            <Meter value={playersNum(game.players)} max={maxPlayers} />
          </p>
        </div>
      </div>

      <div className="mt-3 sm:mt-4 flex items-stretch gap-2.5 sm:gap-3">
        <div data-testid="game-card-bonus" className="flex-1 flex items-center gap-2 sm:gap-2.5 bg-[#013013]/80 border border-dashed border-[#ffb224]/40 rounded-xl px-2.5 sm:px-3 py-1.5 sm:py-2">
          <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#ffb224]/15 flex items-center justify-center shrink-0"><Gift size={18} className="text-[#ffb224]" /></span>
          <span className="leading-tight min-w-0">
            <b className="block text-[#ffb224] text-base sm:text-lg font-extrabold">₹{game.bonus}</b>
            <span className="text-emerald-50/65 text-[11px] sm:text-xs whitespace-nowrap">Sign-up bonus</span>
          </span>
        </div>
        <span className="group/btn relative flex-[1.15] flex">
        <Corners color="border-[#ffb224]/80" size="w-4 h-4" />
        <button
          data-testid={`game-card-download-${game.slug}`}
          onClick={onDownload}
          className="relative overflow-hidden w-full flex items-center justify-center gap-1.5 sm:gap-2 bg-gradient-to-b from-[#ffc552] to-[#ffb224] text-[#062a13] font-extrabold text-[13px] sm:text-[15px] py-2 sm:py-0 rounded-lg sm:rounded-xl border-b-[3px] sm:border-b-[4px] border-[#b47400] shadow-[inset_0_2px_0_rgba(255,255,255,0.45),0_8px_18px_-8px_rgba(255,178,36,0.7)] hover:from-[#ffd070] hover:to-[#ffbd3d] active:border-b-0 active:translate-y-[4px] active:shadow-none transition-all duration-150"
        >
          {game.warning ? <><AlertTriangle size={15} strokeWidth={2.6} /> Warning!!</> : <>Download <Download size={15} strokeWidth={2.6} className="group-hover:translate-y-0.5 transition-transform" /></>}
        </button>
        </span>
      </div>
    </article>
  );
};

const GameGrid = ({ games, title, query, activeTab, setActiveTab }) => {
  const [sort, setSort] = useState("latest");
  const [menu, setMenu] = useState(false);
  const [shown, setShown] = useState(20);

  const filtered = useMemo(() => {
    const base = query ? games.filter((g) => g.name.toLowerCase().includes(query.toLowerCase())) : games;
    const list = [...base];
    if (sort === "players") list.sort((a, b) => playersNum(b.players) - playersNum(a.players));
    if (sort === "bonus") list.sort((a, b) => b.bonus - a.bonus);
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [games, query, sort]);

  const maxPlayers = Math.max(...games.map((g) => playersNum(g.players)));

  return (
    <section id="games" className="max-w-6xl mx-auto px-4 pt-6 pb-6 scroll-mt-20">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 sm:mb-5">
        <h2 className="text-[22px] sm:text-3xl font-extrabold text-white flex items-center gap-2.5 sm:gap-3">
          {title}
          <span data-testid="game-count" className="bg-[#15562e] text-[#ffb224] text-sm font-extrabold px-3 py-1 rounded-lg border-b-[3px] border-[#0a3a1b]">{filtered.length}</span>
        </h2>
        <div className="flex items-center gap-2">
          {setActiveTab && (
            <div className="inline-flex bg-[#073c1b] border border-[#1c5f35] rounded-xl p-1 gap-1">
              <button data-testid="hero-tab-old" onClick={() => setActiveTab("old")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${activeTab === "old" ? "bg-[#ffb224] text-[#062a13]" : "text-emerald-50/80 hover:text-white"}`}><LayoutGrid size={15} /> Old</button>
              <button data-testid="hero-tab-new" onClick={() => setActiveTab("new")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${activeTab === "new" ? "bg-[#ffb224] text-[#062a13]" : "text-emerald-50/80 hover:text-white"}`}><Zap size={15} /> New</button>
            </div>
          )}
          <div className="relative group">
            <Corners size="w-4 h-4" color="border-[#ffb224]/60" />
            <button
              data-testid="sort-btn"
              onClick={() => setMenu(!menu)}
              className="flex items-center gap-2 bg-[#15562e] text-white font-bold text-sm px-4 py-2.5 rounded-xl border-b-[3px] border-[#0a3a1b] active:border-b-0 active:translate-y-[3px] transition-all"
            >
              <span className="text-emerald-50/70 font-medium">Sort by</span> {SORTS.find(([k]) => k === sort)[1]} <ChevronDown size={15} className={`transition-transform ${menu ? "rotate-180" : ""}`} />
            </button>
            {menu && (
              <ul data-testid="sort-menu" className="absolute right-0 mt-2 w-44 bg-[#073c1b] border border-[#1c5f35] rounded-xl overflow-hidden shadow-2xl z-20">
                {SORTS.map(([k, label]) => (
                  <li key={k}>
                    <button data-testid={`sort-${k}`} onClick={() => { setSort(k); setMenu(false); }} className={`w-full text-left px-4 py-2.5 text-sm font-semibold hover:bg-[#15562e] ${sort === k ? "text-[#ffb224]" : "text-white"}`}>{label}</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-emerald-50/50">
          <SearchX size={40} />
          <p className="font-semibold">No games to show yet.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.slice(0, shown).map((game, i) => (
              <GameCard key={game.slug} game={game} rank={i + 1} maxPlayers={maxPlayers} />
            ))}
          </div>
          {shown < filtered.length && (
            <div className="flex justify-center mt-6">
              <span className="group relative inline-flex">
              <Corners color="border-[#ffb224]/70" />
              <button data-testid="show-more-btn" onClick={() => setShown(shown + 20)} className="bg-[#15562e] hover:bg-[#1c6b3a] text-white font-extrabold px-7 py-3 rounded-xl border-b-[4px] border-[#0a3a1b] active:border-b-0 active:translate-y-[4px] transition-all">
                Show more games ({filtered.length - shown} left)
              </button>
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default GameGrid;
